import { ChevronLeft, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { SEOHead } from "@/components/SEOHead";

// Matches the LinkedIn glyph used in Footer.tsx -- lucide-react ships no brand icons.
const LinkedinIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Team"
        description="Meet the team building Meshly, the AI verification layer for the systems your business runs on. Founded by Sandra Bustos."
        canonicalUrl="https://www.meshly.ai/about"
      />
      <Header />

      <div className="container mx-auto px-4 py-16">
        <Button
          variant="ghost"
          className="mb-8 group"
          onClick={() => navigate("/")}
        >
          <ChevronLeft className="h-4 w-4 mr-1 transition-transform group-hover:-translate-x-1" />
          Back to Home
        </Button>

        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-2">
              Meet the team <span className="text-2xl md:text-3xl align-middle">🐙</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src="/team/sandra-bustos.png"
                    alt="Sandra Bustos"
                    className="w-24 h-24 rounded-2xl object-cover flex-shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-sans text-xl font-bold">Sandra Bustos</h2>
                      <a
                        href="https://www.linkedin.com/in/sandrabustos/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Sandra Bustos on LinkedIn"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <LinkedinIcon className="h-4 w-4" />
                      </a>
                    </div>
                    <p className="text-sm font-semibold text-primary mb-2">CEO &amp; Co-founder</p>
                    <p className="text-sm text-muted-foreground">
                      A decade in GTM at Talkdesk, Autodesk, and Airkit, with P&amp;L
                      accountability across multiple segments and go-to-market models.
                    </p>
                  </div>
                </div>
                <p className="text-lg font-semibold border-l-2 border-primary pl-4">
                  "I care about building tools that make complex systems understandable, and
                  help operators trust the data they work with every day."
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src="/team/jeff-whitehead.png"
                    alt="Jeff Whitehead"
                    className="w-24 h-24 rounded-2xl object-cover flex-shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-sans text-xl font-bold">Jeff Whitehead</h2>
                      <a
                        href="https://www.linkedin.com/in/0jwhitehead/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Jeff Whitehead on LinkedIn"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <LinkedinIcon className="h-4 w-4" />
                      </a>
                    </div>
                    <p className="text-sm font-semibold text-primary mb-2">CTO &amp; Co-founder</p>
                    <p className="text-sm text-muted-foreground">
                      Known around Meshly as JW. Started his career at Netscape, helped take
                      Shutterfly public as CIO, and most recently was engineering director at
                      Confluent.
                    </p>
                  </div>
                </div>
                <p className="text-lg font-semibold border-l-2 border-primary pl-4">
                  "The real edge in AI isn't the model, it's the harness around it. Meshly is an
                  explainable cash register: every step inspectable, nothing hidden in a black
                  box."
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-muted/30 mb-20">
            <CardContent className="p-6 flex items-start gap-4">
              <img
                src="/team/gautier-mary-dauphin.png"
                alt="Gautier Mary-Dauphin"
                className="w-16 h-16 rounded-full object-cover flex-shrink-0"
              />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">Gautier Mary-Dauphin</span>
                  <a
                    href="https://www.linkedin.com/in/gautier-mary-dauphin-a4894a4a/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Gautier Mary-Dauphin on LinkedIn"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <LinkedinIcon className="h-3.5 w-3.5" />
                  </a>
                  <span className="text-sm font-semibold text-primary">Finance Advisor</span>
                </div>
                <p className="text-sm text-foreground/70 mt-0.5">
                  Built and scaled the foundations of financial trust across audit, finance
                  transformation, and fintech infrastructure, at Autodesk, Ripple, and EY. He
                  pressure-tests every decision against what enterprise CFOs actually buy.
                </p>
              </div>
            </CardContent>
          </Card>

          <figure className="mb-20 flex flex-col items-center">
            <img
              src="/team/times-square.jpg"
              alt="Sandra and JW in Times Square, standing under the Meshly billboard, Sandra holding a plush octopus"
              className="w-full max-w-sm rounded-2xl object-contain"
            />
            <figcaption className="text-sm text-muted-foreground text-center mt-3">
              Sandra and JW in NYC celebrating Meshly's Times Square debut.
            </figcaption>
          </figure>

          <div className="space-y-6 text-lg text-foreground/80 leading-relaxed mb-20">
            <p>
              That gap between systems is where revenue leaks, audits stall, and diligence finds
              the thing nobody knew was there.
            </p>
            <p>
              Before AI, armies of people in the back office caught it by hand. That labor is
              being cut for efficiency exactly as agents move in to do the work, so the people
              who held the line are leaving right when the machines arrive with no rails.
            </p>
            <p className="text-foreground font-medium">
              That's what Sandra and JW are building: the rails. The model underneath is
              swappable; the rails are what lasts, every action, human or agent, traceable back
              to its source. The next audit won't just be about the numbers. It will be about
              who and what decided them.
            </p>
            <p>
              <Link to="/verification-layer" className="text-primary hover:underline font-semibold">
                See how the rails work: the verification layer →
              </Link>
            </p>
          </div>

          <Card className="border border-primary/20 bg-gradient-to-br from-primary/[0.03] to-transparent">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-md flex-shrink-0">
                  <ShieldCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-2">Built to be trusted</h2>
                  <p className="text-foreground/80">
                    Meshly completed its SOC 2 Type II attestation, tested and attested by
                    Prescient Assurance. Auditability isn't a feature we added, it's the reason
                    the company exists. See our{" "}
                    <a
                      href="https://app.vanta.com/meshly.ai/trust/b667n0dd22c1b8xg5yqvf8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-semibold"
                    >
                      Trust Center
                    </a>{" "}
                    or read more on our{" "}
                    <button
                      onClick={() => navigate("/security")}
                      className="text-primary hover:underline font-semibold"
                    >
                      Security page
                    </button>
                    .
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default About;
